import React from 'react';
import { useSelector } from 'react-redux';
import { motion } from 'framer-motion';

const UnauthorizedUserPermissions = ({ permissionsKey, method }) => {
  const darkMode = useSelector((state) => state.settings.darkMode);
  const defaultLanguage = useSelector((state) => state.settings.defaultLanguage);
  
  // Multi-language content
  const translations = {
    en: {
      title: "Access Restricted",
      description: "You don't have permission to perform this action",
      contactMessage: "To modify permissions, please contact your system administrator or doctor.",
      goBack: "Go Back",
        needAccess: "Need Access?",
      permissions: {
        patients: {
          title: "Patients Management",
          description: "Access patient records and information",
          get: "View patient data",
          put: "Update patient information",
          post: "Add new patients",
          delete: "Remove patients"
        },
        payments: {
          title: "Payments Processing",
          description: "Handle financial transactions and billing",
          get: "View payment history",
          put: "Update payment records",
          post: "Create new payments",
          delete: "Remove payment entries"
        },
        visits: {
          title: "Visits Management",
          description: "Manage patient visits and consultations",
          get: "View visit history",
          put: "Update visit details",
          post: "Schedule new visits",
          delete: "Cancel visits"
        },
        appointments: {
          title: "Appointments",
          description: "Schedule and manage appointments",
          get: "View appointments",
          put: "Reschedule appointments",
          post: "Create appointments",
          delete: "Cancel appointments"
        },
        medications: {
          title: "Medications",
          description: "Prescribe and manage medications",
          get: "View prescriptions",
          put: "Update prescriptions",
          post: "Prescribe medications",
          delete: "Remove prescriptions"
        },
        labTests: {
          title: "Lab Tests",
          description: "Order and review lab tests",
          get: "View test results",
          put: "Update test orders",
          post: "Order new tests",
          delete: "Cancel test orders"
        },
        reports: {
          title: "Reports",
          description: "Generate and access medical reports",
          get: "View reports",
          put: "Update reports",
          post: "Create reports",
          delete: "Remove reports"
        }
      },
      methodLabels: {
        get: "View",
        put: "Update",
        post: "Create",
        delete: "Delete"
      }
    },
    ar: {
      title: "الوصول مقيد",
      description: "ليس لديك إذن لأداء هذا الإجراء",
      contactMessage: "لتعديل الأذونات، يرجى الاتصال بمسؤول النظام أو طبيبك.",
      goBack: "العودة",
        needAccess: "هل تحتاج إلى وصول؟",
      permissions: {
        patients: {
          title: "إدارة المرضى",
          description: "الوصول إلى سجلات ومعلومات المرضى",
          get: "عرض بيانات المرضى",
          put: "تحديث معلومات المرضى",
          post: "إضافة مرضى جدد",
          delete: "حذف المرضى"
        },
        payments: {
          title: "معالجة المدفوعات",
          description: "إدارة المعاملات المالية والفواتير",
          get: "عرض سجل المدفوعات",
          put: "تحديث سجلات المدفوعات",
          post: "إنشاء مدفوعات جديدة",
          delete: "حذف إدخالات المدفوعات"
        },
        visits: {
          title: "إدارة الزيارات",
          description: "إدارة زيارات واستشارات المرضى",
          get: "عرض سجل الزيارات",
          put: "تحديث تفاصيل الزيارات",
          post: "جدولة زيارات جديدة",
          delete: "إلغاء الزيارات"
        },
        appointments: {
          title: "المواعيد",
          description: "جدولة وإدارة المواعيد",
          get: "عرض المواعيد",
          put: "إعادة جدولة المواعيد",
          post: "إنشاء مواعيد",
          delete: "إلغاء المواعيد"
        },
        medications: {
          title: "الأدوية",
          description: "وصف وإدارة الأدوية",
          get: "عرض الوصفات الطبية",
          put: "تحديث الوصفات الطبية",
          post: "وصف أدوية",
          delete: "إزالة الوصفات الطبية"
        },
        labTests: {
          title: "الفحوصات المخبرية",
          description: "طلب ومراجعة الفحوصات المخبرية",
          get: "عرض نتائج الفحوصات",
          put: "تحديث طلبات الفحوصات",
          post: "طلب فحوصات جديدة",
          delete: "إلغاء طلبات الفحوصات"
        },
        reports: {
          title: "التقارير",
          description: "إنشاء والوصول إلى التقارير الطبية",
          get: "عرض التقارير",
          put: "تحديث التقارير",
          post: "إنشاء تقارير",
          delete: "حذف التقارير"
        }
      },
      methodLabels: {
        get: "عرض",
        put: "تحديث",
        post: "إنشاء",
        delete: "حذف"
      }
    },
    fr: {
      title: "Accès Restreint",
      description: "Vous n'avez pas la permission d'effectuer cette action",
      contactMessage: "Pour modifier les autorisations, veuillez contacter votre administrateur système ou votre médecin.",
      goBack: "Retour",
        needAccess: "Besoin d'accès ?",
      permissions: {
        patients: {
          title: "Gestion des Patients",
          description: "Accéder aux dossiers et informations des patients",
          get: "Voir les données des patients",
          put: "Mettre à jour les informations des patients",
          post: "Ajouter de nouveaux patients",
          delete: "Supprimer des patients"
        },
        payments: {
          title: "Traitement des Paiements",
          description: "Gérer les transactions financières et la facturation",
          get: "Voir l'historique des paiements",
          put: "Mettre à jour les dossiers de paiement",
          post: "Créer de nouveaux paiements",
          delete: "Supprimer des entrées de paiement"
        },
        visits: {
          title: "Gestion des Visites",
          description: "Gérer les visites et consultations des patients",
          get: "Voir l'historique des visites",
          put: "Mettre à jour les détails des visites",
          post: "Planifier de nouvelles visites",
          delete: "Annuler des visites"
        },
        appointments: {
          title: "Rendez-vous",
          description: "Planifier et gérer les rendez-vous",
          get: "Voir les rendez-vous",
          put: "Reprogrammer les rendez-vous",
          post: "Créer des rendez-vous",
          delete: "Annuler des rendez-vous"
        },
        medications: {
          title: "Médicaments",
          description: "Prescrire et gérer les médicaments",
          get: "Voir les prescriptions",
          put: "Mettre à jour les prescriptions",
          post: "Prescrire des médicaments",
          delete: "Supprimer des prescriptions"
        },
        labTests: {
          title: "Tests de Laboratoire",
          description: "Commander et examiner les tests de laboratoire",
          get: "Voir les résultats des tests",
          put: "Mettre à jour les commandes de tests",
          post: "Commander de nouveaux tests",
          delete: "Annuler les commandes de tests"
        },
        reports: {
          title: "Rapports",
          description: "Générer et accéder aux rapports médicaux",
          get: "Voir les rapports",
          put: "Mettre à jour les rapports",
          post: "Créer des rapports",
          delete: "Supprimer des rapports"
        }
      },
      methodLabels: {
        get: "Voir",
        put: "Mettre à jour",
        post: "Créer",
        delete: "Supprimer"
      }
    }
  };

  const t = translations[defaultLanguage] || translations.en;
  const permissionType = permissionsKey.replace('Permissions', '');
  const permissionInfo = t.permissions[permissionType];
  const methodLabel = t.methodLabels[method];
  
  // Determine dependent permissions based on visitsPermissions
  const isDependentPermission = ['medications', 'labTests', 'reports'].includes(permissionType);
  const dependsOnVisits = isDependentPermission;
  
  const getPermissionMessage = () => {
    if (!permissionInfo) return '';
    
    const baseMessage = `${t.description}: ${permissionInfo.title} - ${permissionInfo[method] || ''}`;
    
    if (dependsOnVisits) {
      return `${baseMessage}. ${t.permissions.visits.title} access is required for this feature.`;
    }
    
    return baseMessage;
  };

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.6,
        when: "beforeChildren",
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: { opacity: 1, x: 0 }
  };

  const iconVariants = {
    hidden: { scale: 0, rotate: -180 },
    visible: { 
      scale: 1, 
      rotate: 0,
      transition: {
        type: "spring",
        stiffness: 260,
        damping: 20
      }
    }
  };

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      className={`w-full h-full flex items-center justify-center  ${
        darkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-800'
      }`}
      dir={defaultLanguage === 'ar' ? 'rtl' : 'ltr'}
    >
      <motion.div
        variants={containerVariants}
        className={`w-full h-full p-8 rounded-2xl shadow-lg flex flex-col  justify-center ${
          darkMode 
            ? 'bg-red-700/10 border border-red-700' 
            : 'bg-red-50/10 border border-red-300'
        }`}
      >
        {/* Icon */}
        <motion.div
          variants={iconVariants}
          className="flex justify-center mb-6"
        >
          <div className={`p-4 rounded-full ${
            darkMode ? 'bg-red-900/20 text-red-400' : 'bg-red-100 text-red-600'
          }`}>
            <svg 
              className="w-12 h-12" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24" 
              xmlns="http://www.w3.org/2000/svg"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M12 15v2m0 0v2m0-2h2m-2 0H8m6-9V6a4 4 0 00-8 0v3h8zM5 21h14a2 2 0 002-2V11a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" 
              />
            </svg>
          </div>
        </motion.div>

        {/* Title */}
        <motion.h2 
          variants={itemVariants}
          className={`text-2xl font-bold text-center mb-2 max-sm:text-lg ${
            darkMode ? '!text-red-200' : '!text-red-800'
          }`}
        >
          {t.title}
        </motion.h2>

        {/* Description */}
        <motion.p 
          variants={itemVariants}
          className={`text-center mb-6   max-sm:text-xs ${
            darkMode ? 'text-red-200' : 'text-red-800'
          }`}
        >
          {getPermissionMessage()}
        </motion.p>

        {/* Permission Details */}
        <motion.div 
          variants={itemVariants}
          className={`p-6 rounded-lg mb-6 ${
            darkMode ? 'bg-gray-700' : 'bg-gray-100'
          }`}
        >
          <div className={`flex items-start mb-4 ${defaultLanguage === 'ar' ? 'flex-row-reverse text-right' : ''}`}>
            <div className={`p-2 rounded ${defaultLanguage === 'ar' ? 'ml-3 mr-0' : 'mr-3'} ${
              darkMode ? 'bg-gray-600' : 'bg-white'
            }`}>
              <svg 
                className="w-6 h-6 text-yellow-500" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24" 
                xmlns="http://www.w3.org/2000/svg"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" 
                />
              </svg>
            </div>
            <div className={`${defaultLanguage === 'ar' ? 'text-right' : ''}`}>
              <h3 className="font-semibold mb-1 max-sm:text-sm">
                {permissionInfo?.title || permissionType}
              </h3>
              <p className={`text-sm text-gray-500 dark:text-gray-400 max-sm:text-xs ${defaultLanguage === 'ar' ? 'text-right' : ''}`}>
                {permissionInfo?.description || ''}
              </p>
            </div>
          </div>

          {/* Method Badge */}
          <div className={`inline-block px-3 py-1 rounded-full text-sm font-medium max-sm:text-xs ${
            darkMode ? 'bg-red-900 text-red-300' : 'bg-red-100 text-red-800'
          }`}>
            {methodLabel}: {method.toUpperCase()}
          </div>

          {/* Dependency Warning */}
          {dependsOnVisits && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className={`mt-4 p-3 rounded-lg text-sm ${
                darkMode ? 'bg-yellow-900/20 text-yellow-300' : 'bg-yellow-50 text-yellow-800'
              }`}
            >
              <div className={`flex items-center ${defaultLanguage === 'ar' ? 'flex-row-reverse' : ''}`}>
                <svg 
                  className={`w-4 h-4 ${defaultLanguage === 'ar' ? 'ml-2' : 'mr-2'}`} 
                  fill="currentColor" 
                  viewBox="0 0 20 20" 
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path 
                    fillRule="evenodd" 
                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" 
                    clipRule="evenodd" 
                  />
                </svg>
                <span className={`max-sm:text-xsm ${defaultLanguage === 'ar' ? 'text-right' : ''}`}>
                  {t.permissions.visits.title} permission is required for this action
                </span>
              </div>
            </motion.div>
          )}
        </motion.div>

        {/* Contact Message */}
        <motion.div 
          variants={itemVariants}
          className={`p-4 rounded-lg mb-6 ${
            darkMode ? 'bg-blue-900/20 border border-blue-800' : 'bg-blue-50 border border-blue-200'
          }`}
        >
          <div className={`flex items-center mb-2 ${defaultLanguage === 'ar' ? 'flex-row-reverse' : ''}`}>
            <svg 
              className={`w-5 h-5 ${defaultLanguage === 'ar' ? 'ml-2' : 'mr-2'} ${
                darkMode ? 'text-blue-400' : 'text-blue-600'
              }`} 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24" 
              xmlns="http://www.w3.org/2000/svg"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" 
              />
            </svg>
            <span className={`font-medium max-sm:text-xsm ${defaultLanguage === 'ar' ? 'text-right' : ''}`}>
              {t.needAccess}
            </span>
          </div>
          <p className="text-sm max-sm:text-xs">
            {t.contactMessage}
          </p>
        </motion.div>

        
      </motion.div>
    </motion.div>
  );
};

export default UnauthorizedUserPermissions;